export default defineNuxtRouteMiddleware(async (to) => {
  if (import.meta.server) return

  // Defensively clear any stale error state (e.g. a previous 404 from
  // this same middleware) before doing anything else. Confirmed live:
  // after one denied /admin/* attempt, a subsequent attempt showed 404
  // again immediately, with ZERO network requests for the permission
  // check -- meaning this middleware's own fresh logic below never got
  // to run at all. The console showed '[nuxt] error caught during app
  // initialization Pa: Page not found', suggesting Nuxt's error state
  // was being reinstated before this function's own checks executed,
  // not a fresh 404 from a real (re-run) check. clearError() with no
  // redirect just wipes that state without navigating anywhere --
  // the rest of this middleware still runs normally afterward and
  // decides the real destination fresh.
  clearError()

  const { isSignedIn, isLoaded, role, user, email, displayName, getToken } = useClerkUser()
  const { apiBase: API } = useRuntimeConfig().public
  const { hasPermission, ensureFreshPermissions } = usePermissions()

  // Previously: `if (!isLoaded.value) return` -- if Clerk hadn't finished
  // initializing yet (a real, common race on a direct URL navigation,
  // e.g. typing /admin/users into the address bar rather than clicking a
  // link within an already-loaded app), this let navigation proceed with
  // NO check at all rather than a blocked one. Since nothing re-runs this
  // middleware afterward on the same page load, the page just stayed
  // loaded, fully unguarded -- confirmed live: an archobbyist account
  // loading the full admin Users page via direct navigation.
  //
  // Fix: actually wait for Clerk to finish loading before deciding
  // anything, with a safety timeout so navigation can never hang forever
  // if Clerk fails to initialize for some other reason.
  if (!isLoaded.value) {
    await new Promise<void>((resolve) => {
      const stop = watch(isLoaded, (loaded) => {
        if (loaded) { stop(); resolve() }
      })
      setTimeout(() => { stop(); resolve() }, 3000)
    })
  }

  const path = to.path

  // Sync signed-in user to arc_users on every route (INSERT OR IGNORE — safe, fire and forget)
  if (isSignedIn.value && user.value) {
    // The sync endpoint is auth-gated now (self-only, identity from the
    // verified token) -- fire-and-forget stays, just with the token on.
    const syncToken = await getToken()
    fetch(`${API}/manage/users`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${syncToken}` },
      body: JSON.stringify({
        clerk_id: user.value.id,
        email: email.value,
        username: user.value.username ?? displayName.value,
        role: role.value || 'archobbyist',
      }),
    }).catch(() => {})
  }

  // Fetch permissions/role for every signed-in page load, not just
  // admin routes -- the identity banner in the nav shows role on EVERY
  // page, and this was previously only called inside the /admin gate
  // below, meaning permRole stayed at its initial null/"Member" default
  // for anyone viewing that banner from anywhere else (the homepage
  // right after sign-in, in particular -- exactly where a brand new
  // account, or an existing one opening a fresh tab, actually lands).
  if (isSignedIn.value && user.value) {
    await ensureFreshPermissions(user.value.id)
  }

  // Admin routes require the admin.access permission -- data-driven via
  // role_permissions, not a hardcoded role name.
  //
  // Denied requests get a genuine 404, not a redirect to /profile.
  // Redirecting somewhere specific confirms "yes, this URL is real, you
  // just can't see it" -- which invites probing other /admin/* paths to
  // map out what exists. A 404 is indistinguishable from a route that
  // was never there at all, and the URL bar never changes either way.
  if (path.startsWith('/admin')) {
    // Signed-out probers get the same 404 as unauthorized ones -- a
    // sign-in redirect (or even a homepage redirect) confirms the URL
    // is real and protected. 404 reveals nothing.
    if (!isSignedIn.value) {
      throw createError({ statusCode: 404, statusMessage: 'Page not found', fatal: false })
    }
    // Permissions already fetched unconditionally above for every
    // signed-in page load -- no need to call it again here.
    // If permission fetch failed entirely, fall back to role check from Clerk JWT
    // This prevents a CORS/network failure from locking out legitimate admins
    const adminRoles = ['master_admin', 'admin', 'convention_manager', 'event_manager']
    const hasAdminAccess = hasPermission('admin.access') || adminRoles.includes(role.value)
    if (!hasAdminAccess) {
      throw createError({ statusCode: 404, statusMessage: 'Page not found', fatal: false })
    }

    // Per-area gating: inside the admin shell, each wing needs its own
    // area permission. Same 404 policy -- a denied wing is indistinguishable
    // from one that doesn't exist. master_admin passes everything (its
    // my-permissions response is the full catalog). Legacy role fallback
    // keeps founding admins working even if the permission fetch failed.
    const AREA_GATES: Array<[string, string]> = [
      ['/admin/users',      'users.access'],
      ['/admin/archive',    'archive.access'],
      ['/admin/publishing', 'publishing.access'],
      ['/admin/media',      'media.access'],
    ]
    const legacyFull = ['master_admin', 'admin'].includes(role.value)
    for (const [prefix, perm] of AREA_GATES) {
      if (path.startsWith(prefix) && !hasPermission(perm) && !legacyFull) {
        throw createError({ statusCode: 404, statusMessage: 'Page not found', fatal: false })
      }
    }
  }

  // Profile requires sign-in
  if (path === '/profile') {
    if (!isSignedIn.value) return navigateTo('/sign-in')
  }
})